import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Layout,
  Dropdown,
  Avatar,
  Space,
  Modal,
  Form,
  Input,
  message,
  Button,
  Tooltip,
  Spin,
  DatePicker,
  InputNumber,
} from "antd";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ShoppingCartOutlined,
  PlusOutlined,
  LineChartOutlined,
} from "@ant-design/icons";
import Sidebar from "./Sidebar";
import Language from "../../features/shared/pages/Language";
import usePermissions from "../../hooks/usePermissions";
import { apiClient, SESSION_EXPIRED_EVENT } from "../../shared/api/AllApi";
import {
  fetchExchangeRates,
  createExchangeRate,
  pickEffectiveExchangeRate,
  dispatchExchangeRatesUpdated,
  EXCHANGE_RATES_UPDATED_EVENT,
} from "../../shared/lib/exchangeRates";
import { fetchSystemConfig, readCachedSystemConfig } from "../../shared/lib/systemConfig";
import "../../shared/styles/AppLayout.css";

const { Header, Content } = Layout;

const SIDEBAR_COLLAPSED_KEY = "velan_sidebar_collapsed";

const AppLayout = ({ children, cartCount }) => {
  const { t, i18n } = useTranslation();
  const { hasPermission } = usePermissions();
  const canManageExchangeRates = hasPermission("users.index");
  const [profileForm] = Form.useForm();
  const [quickFxForm] = Form.useForm();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved === "true";
  });
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const raw = localStorage.getItem("sessionUser");
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  });
  const [headerSystemConfig, setHeaderSystemConfig] = useState(() => readCachedSystemConfig());
  const [headerFxRows, setHeaderFxRows] = useState([]);
  const [headerFxLoading, setHeaderFxLoading] = useState(false);
  const [quickFxOpen, setQuickFxOpen] = useState(false);
  const [quickFxSaving, setQuickFxSaving] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [sessionLoginLoading, setSessionLoginLoading] = useState(false);
  const [sessionForm] = Form.useForm();

  const loadNavbarFx = useCallback(async () => {
    setHeaderFxLoading(true);
    try {
      const cfg = await fetchSystemConfig();
      setHeaderSystemConfig(cfg);
      if (!cfg?.secondary_currency_code) {
        setHeaderFxRows([]);
        return;
      }
      const list = await fetchExchangeRates();
      setHeaderFxRows(Array.isArray(list) ? list : []);
    } catch {
      setHeaderFxRows([]);
    } finally {
      setHeaderFxLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNavbarFx();
  }, [loadNavbarFx]);

  useEffect(() => {
    window.addEventListener(EXCHANGE_RATES_UPDATED_EVENT, loadNavbarFx);
    return () => window.removeEventListener(EXCHANGE_RATES_UPDATED_EVENT, loadNavbarFx);
  }, [loadNavbarFx]);

  useEffect(() => {
    const handleSessionExpired = () => {
      setSessionExpired(true);
      sessionForm.resetFields();
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [sessionForm]);

  const mainCurrencyCode = headerSystemConfig?.currency_code || "IQD";
  const secondaryCurrencyCode = (headerSystemConfig?.secondary_currency_code || "").trim();
  const showFxNavbar = Boolean(secondaryCurrencyCode);

  const effectiveFx = useMemo(
    () => pickEffectiveExchangeRate(headerFxRows, dayjs()),
    [headerFxRows]
  );

  const openQuickFx = () => {
    quickFxForm.resetFields();
    quickFxForm.setFieldsValue({
      effective_from: dayjs(),
      main_per_secondary: undefined,
      note: "",
    });
    setQuickFxOpen(true);
  };

  const handleQuickFxSave = async () => {
    try {
      const values = await quickFxForm.validateFields();
      const payload = {
        effective_from: values.effective_from.format("YYYY-MM-DD"),
        main_per_secondary: values.main_per_secondary,
        note: values.note || null,
      };
      setQuickFxSaving(true);
      await createExchangeRate(payload);
      message.success(t("exchangeRatesAdded"));
      setQuickFxOpen(false);
      dispatchExchangeRatesUpdated();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || t("exchangeRatesSaveFailed"));
    } finally {
      setQuickFxSaving(false);
    }
  };

  const handleSidebarCollapse = (collapsed) => {
    setSidebarCollapsed(collapsed);
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  };

  useEffect(() => {
    const savedLang = localStorage.getItem("selectedLanguage") || "en";
    if (savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang);
    }
  }, [i18n]);

  const profileMenuItems = [
    {
      key: "edit-profile",
      label: t("editProfile"),
    },
    {
      key: "logout",
      icon: <LogoutOutlined style={{ color: "#ff4d4f" }} />,
      label: t("logout"),
    },
  ];

  const readSessionUser = () => {
    try {
      const raw = localStorage.getItem("sessionUser");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };

  // Pre-fill profile from the same session we already have from login (no extra GET /profile).
  // Other app APIs are open; only PUT /profile uses Sanctum — token must be non-empty (see AllApi.js).
  useEffect(() => {
    if (!profileModalOpen) return;
    const u = readSessionUser();
    profileForm.setFieldsValue({ // eslint-disable-line react-hooks/exhaustive-deps
      name: u?.name ?? "",
      email: u?.email ?? "",
      password: "",
      password_confirmation: "",
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileModalOpen]);

  const handleProfileMenuClick = ({ key }) => {
    if (key === "edit-profile") {
      setProfileModalOpen(true);
    }
    if (key === "logout") {
      localStorage.removeItem("sessionToken");
      localStorage.removeItem("sessionUser");
      localStorage.removeItem("sessionPermissions");
      window.location.href = "/";
    }
  };

  const handleSaveProfile = async () => {
    try {
      const values = await profileForm.validateFields();
      const payload = {
        name: values.name,
        email: values.email || undefined,
      };
      if (values.password) {
        payload.password = values.password;
        payload.password_confirmation = values.password_confirmation;
      }

      setProfileSaving(true);
      const res = await apiClient.put("/profile", payload);
      const updatedUser = res?.data?.user || { ...currentUser, ...payload };
      setCurrentUser(updatedUser);
      localStorage.setItem("sessionUser", JSON.stringify(updatedUser));
      message.success(t("profileUpdated"));
      setProfileModalOpen(false);
      profileForm.resetFields();
    } catch (error) {
      if (error?.errorFields) return;
      const data = error?.response?.data;
      const serverMsg = data?.message;
      const firstFieldError = data?.errors
        ? Object.values(data.errors).flat().find(Boolean)
        : null;
      message.error(firstFieldError || serverMsg || t("profileUpdateError"));
    } finally {
      setProfileSaving(false);
    }
  };

  const handleSessionReLogin = async () => {
    try {
      const values = await sessionForm.validateFields();
      setSessionLoginLoading(true);
      const { apiPost } = await import("../../utils/apiHelpers");
      const response = await apiPost("login", { email: values.email, password: values.password });
      const token = response?.data?.token;
      const user = response?.data?.user;
      const permissions = response?.data?.permissions;
      if (token) {
        localStorage.setItem("sessionToken", token);
        if (user) localStorage.setItem("sessionUser", JSON.stringify(user));
        if (permissions) localStorage.setItem("sessionPermissions", JSON.stringify(permissions));
        setCurrentUser(user || currentUser);
        setSessionExpired(false);
        sessionForm.resetFields();
        message.success(t("profileUpdated"));
      } else {
        message.error(t("invalid_credentials"));
      }
    } catch (err) {
      if (err?.errorFields) return;
      message.error(t("invalid_credentials"));
    } finally {
      setSessionLoginLoading(false);
    }
  };

  return (
    <Layout className="app-layout">
      <Sidebar
        collapsed={sidebarCollapsed}
        onCollapse={handleSidebarCollapse}
        cartCount={cartCount}
      />
      <Layout className="main-layout">
        <Header className="app-header">
          <button
            type="button"
            className="sidebar-trigger"
            onClick={() => handleSidebarCollapse(!sidebarCollapsed)}
            aria-label={sidebarCollapsed ? "Expand menu" : "Collapse menu"}
          >
            {sidebarCollapsed ? (
              <MenuUnfoldOutlined className="trigger-icon" />
            ) : (
              <MenuFoldOutlined className="trigger-icon" />
            )}
          </button>
          <div className="header-actions">
            {showFxNavbar && (
              <div className="header-fx-strip">
                <LineChartOutlined className="header-fx-icon" aria-hidden />
                {headerFxLoading ? (
                  <Spin size="small" />
                ) : effectiveFx ? (
                  <Tooltip
                    title={t("navFxTooltip", {
                      date: effectiveFx.effective_from
                        ? dayjs(effectiveFx.effective_from).format("YYYY-MM-DD")
                        : "",
                    })}
                  >
                    <span className="header-fx-rate">
                      {t("navFxSummary", {
                        main: mainCurrencyCode,
                        rate: (Number(effectiveFx.main_per_secondary) * 100).toLocaleString(),
                        secondary: secondaryCurrencyCode,
                      })}
                    </span>
                  </Tooltip>
                ) : (
                  <span className="header-fx-muted">{t("navFxNoRate")}</span>
                )}
                {canManageExchangeRates && (
                  <Space size={4}>
                    <Tooltip title={t("navFxQuickAdd")}>
                      <Button
                        type="default"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={openQuickFx}
                        className="header-fx-quick-btn"
                      />
                    </Tooltip>
                    <Link to="/settings/exchange-rates" className="header-fx-link">
                      {t("navFxAllRates")}
                    </Link>
                  </Space>
                )}
              </div>
            )}
            <a href="/CartShop" className="header-cart">
              <ShoppingCartOutlined className="header-icon" />
              {cartCount > 0 && (
                <span className="cart-badge">{cartCount}</span>
              )}
            </a>
            <div className="header-lang">
              <Language />
            </div>
            <Dropdown
              menu={{ items: profileMenuItems, onClick: handleProfileMenuClick }}
              trigger={["click"]}
              placement="bottomRight"
              getPopupContainer={(node) => node.parentElement || document.body}
              overlayClassName="header-profile-dropdown"
            >
              <button type="button" className="header-profile">
                <Avatar
                  size={28}
                  icon={<UserOutlined />}
                  className="header-avatar"
                />
                <span className="header-profile-text">
                  {currentUser?.name || t("profile")}
                </span>
              </button>
            </Dropdown>
          </div>
        </Header>
        <Content className="app-content">{children}</Content>
      </Layout>

      <Modal
        title={t("exchangeRatesModalAdd")}
        open={quickFxOpen}
        onOk={handleQuickFxSave}
        confirmLoading={quickFxSaving}
        onCancel={() => setQuickFxOpen(false)}
        okText={t("save")}
        cancelText={t("cancel")}
        destroyOnHidden
        centered
      >
        <Form form={quickFxForm} layout="vertical">
          <Form.Item name="effective_from" label={t("exchangeRatesEffectiveFrom")} rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="main_per_secondary"
            label={t("exchangeRatesHowManyLabel", {
              secondary: secondaryCurrencyCode || "?",
              main: mainCurrencyCode,
            })}
            rules={[{ required: true, message: t("exchangeRatesRateRequired") }]}
          >
            <InputNumber min={0.000001} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="note" label={t("exchangeRatesNote")}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("sessionExpiredTitle") || "Session Expired"}
        open={sessionExpired}
        onOk={handleSessionReLogin}
        confirmLoading={sessionLoginLoading}
        onCancel={() => {
          localStorage.removeItem("sessionToken");
          localStorage.removeItem("sessionUser");
          localStorage.removeItem("sessionPermissions");
          window.location.href = "/";
        }}
        okText={t("login")}
        cancelText={t("logout")}
        centered
        closable={false}
        maskClosable={false}
      >
        <p style={{ marginBottom: 16, color: "#64748b" }}>
          {t("sessionExpiredMessage") || "Your session has expired. Please enter your credentials to continue."}
        </p>
        <Form form={sessionForm} layout="vertical" requiredMark={false}>
          <Form.Item
            name="email"
            label={t("email")}
            rules={[{ required: true, message: t("fields_required") }]}
          >
            <Input
              prefix={<span style={{ color: "#94a3b8" }}>✉</span>}
              placeholder={t("enterEmail")}
              autoComplete="email"
            />
          </Form.Item>
          <Form.Item
            name="password"
            label={t("password")}
            rules={[{ required: true, message: t("fields_required") }]}
          >
            <Input.Password
              prefix={<span style={{ color: "#94a3b8" }}>🔒</span>}
              placeholder={t("enterPassword")}
              autoComplete="current-password"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("editProfile")}
        open={profileModalOpen}
        onOk={handleSaveProfile}
        confirmLoading={profileSaving}
        onCancel={() => setProfileModalOpen(false)}
        okText={t("save")}
        cancelText={t("cancel")}
        centered
      >
          <Form form={profileForm} layout="vertical">
          <Form.Item
            name="name"
            label={t("name")}
            rules={[{ required: true, message: t("enterName") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label={t("email")}
            rules={[{ type: "email", message: t("invalidEmail") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="password"
            label={t("newPassword")}
            rules={[
              {
                validator(_, value) {
                  if (value === undefined || value === null || String(value).trim() === "") {
                    return Promise.resolve();
                  }
                  if (String(value).length >= 6) return Promise.resolve();
                  return Promise.reject(new Error(t("passwordMin")));
                },
              },
            ]}
          >
            <Input.Password placeholder={t("leaveBlankKeepPassword")} />
          </Form.Item>
          <Form.Item
            name="password_confirmation"
            label={t("confirmPassword")}
            dependencies={["password"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const password = getFieldValue("password");
                  if (!password && !value) return Promise.resolve();
                  if (value === password) return Promise.resolve();
                  return Promise.reject(new Error(t("passwordsDoNotMatch")));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
          </Form>
      </Modal>
    </Layout>
  );
};

export default AppLayout;
